import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { X, Send, User, Bot, Sparkles } from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'entorid_chat_session';

const WELCOME_MSG = {
  id: 'welcome',
  from: 'bot',
  text: "Hi 👋 Welcome to EntoRid! I'm your pest-control assistant. How can I help you today?",
  quickReplies: [
    { key: 'book', label: 'Book Service' },
    { key: 'pricing', label: 'Check Price' },
    { key: 'pest', label: 'Pest Problem' },
    { key: 'expert', label: 'Talk to Expert' },
  ],
};

const PEST_OPTIONS = [
  { key: 'pest-cockroach', label: 'Cockroach' },
  { key: 'pest-termite', label: 'Termite' },
  { key: 'pest-rodent', label: 'Rodent' },
  { key: 'pest-bedbug', label: 'Bed Bugs' },
  { key: 'pest-mosquito', label: 'Mosquito' },
  { key: 'pest-other', label: 'Something else' },
];

const ESCALATE_BUTTONS = [
  {
    key: 'wa',
    label: 'Chat on WhatsApp',
    color: 'green',
    href: 'https://api.whatsapp.com/send?phone=919410110910&text=Hello%20EntoRid%2C%20I%20was%20chatting%20with%20your%20AI%20assistant%20and%20need%20help.',
  },
  { key: 'call', label: 'Call Now', color: 'blue', href: 'tel:+917819988010' },
];

// Very lightweight client-side detector — spots phone numbers in user text
const PHONE_RE = /(\+?91[\s-]?)?[6-9]\d{9}/;

export default function ChatBot({ open, onClose, onOpenCalculator }) {
  const [messages, setMessages] = useState([WELCOME_MSG]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(SESSION_KEY);
  });
  const [leadCaptured, setLeadCaptured] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const pushUser = (text) => {
    setMessages((prev) => [...prev, { id: `u-${Date.now()}`, from: 'user', text }]);
  };

  const pushBotInstant = (msg) => {
    setMessages((prev) => [...prev, { id: `b-${Date.now()}`, from: 'bot', ...msg }]);
  };

  /** Send a user's free-text message to the AI backend and render reply. */
  const callAI = useCallback(
    async (text) => {
      setIsTyping(true);
      try {
        const { data } = await axios.post(
          `${API_BASE}/chat/message`,
          { session_id: sessionId, message: text },
          { timeout: 25000 }
        );
        if (data?.session_id) {
          setSessionId(data.session_id);
          try { window.localStorage.setItem(SESSION_KEY, data.session_id); } catch { /* noop */ }
        }
        pushBotInstant({
          text: data?.reply || "Sorry, I couldn't reach our AI. Please try WhatsApp or Call.",
          actionButtons: data?.escalate ? ESCALATE_BUTTONS : undefined,
        });

        // If the user's message contains a phone number AND name was earlier given,
        // auto-persist as a lead (best-effort; fire-and-forget).
        const phoneMatch = text.match(PHONE_RE);
        if (phoneMatch && !leadCaptured) {
          tryAutoCaptureLead(text);
        }
      } catch (err) {
        console.error('Chat call failed', err);
        pushBotInstant({
          text: "I'm having trouble reaching our AI right now. Our human team is quicker 👇",
          actionButtons: ESCALATE_BUTTONS,
        });
      } finally {
        setIsTyping(false);
      }
    },
    [sessionId, leadCaptured] // eslint-disable-line react-hooks/exhaustive-deps
  );

  /** Fire a /api/chat/lead request when we've seen name + phone in the conversation. */
  const tryAutoCaptureLead = useCallback(
    async (phoneMessage) => {
      // Guess name from the last few user messages — take the first capitalised word sequence
      const userMsgs = messages.filter((m) => m.from === 'user').map((m) => m.text);
      const combined = [...userMsgs, phoneMessage].join(' ');
      const nameMatch = combined.match(/(?:i am|i'm|my name is|name:?)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i)
        || combined.match(/\b([A-Z][a-z]{2,}(?:\s+[A-Z][a-z]+)?)\b/);
      const phone = (phoneMessage.match(PHONE_RE) || [''])[0].replace(/\D/g, '').slice(-10);
      if (!phone || phone.length !== 10) return;
      const name = nameMatch ? nameMatch[1] : 'Chat Visitor';

      try {
        await axios.post(`${API_BASE}/chat/lead`, {
          session_id: sessionId || 'unknown',
          name,
          phone,
          last_message: userMsgs.slice(-1)[0] || null,
        }, { timeout: 8000 });
        setLeadCaptured(true);
      } catch (err) {
        console.warn('Auto lead capture failed', err?.message);
      }
    },
    [messages, sessionId]
  );

  /** Quick-reply handler — shortcuts bypass the AI for instant UX on obvious intents. */
  const handleQuickReply = (key, label) => {
    pushUser(label);

    if (key === 'book') {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        pushBotInstant({
          text: "Great! Opening our instant price calculator now — you'll get exact pricing in 60 seconds.",
        });
        setTimeout(() => {
          onOpenCalculator?.();
          onClose?.();
        }, 700);
      }, 450);
      return;
    }

    if (key === 'pest') {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        pushBotInstant({
          text: "Which pest are you dealing with? Pick one below or just describe it in your own words.",
          quickReplies: PEST_OPTIONS,
        });
      }, 450);
      return;
    }

    if (key === 'expert') {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        pushBotInstant({
          text: "Connecting you to our expert — choose your preferred way:",
          actionButtons: ESCALATE_BUTTONS,
        });
      }, 450);
      return;
    }

    // For Pricing + specific pest chips + anything else → send to AI for a smart reply
    // (so Claude can adapt pricing / reasoning context instead of static copy)
    callAI(label);
  };

  const handleSend = (e) => {
    e?.preventDefault?.();
    const txt = input.trim();
    if (!txt || isTyping) return;
    pushUser(txt);
    setInput('');
    callAI(txt);
  };

  if (!open) return null;

  return (
    <div
      data-testid="chatbot-panel"
      className="fixed z-[70] bottom-24 right-4 sm:right-5 w-[calc(100vw-2rem)] sm:w-[380px] max-w-[380px] max-h-[75vh] h-[560px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden ring-1 ring-gray-200 animate-slideUp"
      role="dialog"
      aria-label="Chat with EntoRid"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-brand-blue to-brand-blue-light text-white">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative w-10 h-10 rounded-full bg-brand-green/20 flex items-center justify-center shrink-0">
            <Bot size={20} className="text-brand-green-light" />
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-brand-green ring-2 ring-brand-blue" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm flex items-center gap-1.5">
              EntoBot <Sparkles size={12} className="text-brand-green-light" />
            </div>
            <div className="text-xs text-white/70 flex items-center gap-1 truncate">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-green-light animate-pulse" /> AI · Online · replies instantly
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="chatbot-close"
          aria-label="Close chat"
          className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors shrink-0"
        >
          <X size={18} />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollerRef} className="flex-1 overflow-y-auto px-3 py-4 bg-slate-50 space-y-3" data-testid="chatbot-messages">
        {messages.map((m) => <Message key={m.id} msg={m} onQuickReply={handleQuickReply} />)}
        {isTyping && <TypingBubble />}
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="flex items-center gap-2 p-3 border-t border-gray-200 bg-white">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={isTyping ? 'EntoBot is thinking…' : 'Ask me anything about pests…'}
          data-testid="chatbot-input"
          disabled={isTyping}
          className="flex-1 h-10 px-3 rounded-full bg-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/30 placeholder-gray-400 disabled:opacity-60"
          aria-label="Type your message"
        />
        <button
          type="submit"
          data-testid="chatbot-send"
          aria-label="Send message"
          disabled={!input.trim() || isTyping}
          className="w-10 h-10 rounded-full bg-brand-green hover:bg-brand-green-dark text-white flex items-center justify-center disabled:opacity-40 transition-colors"
        >
          <Send size={16} />
        </button>
      </form>
    </div>
  );
}

function Message({ msg, onQuickReply }) {
  if (msg.from === 'user') {
    return (
      <div className="flex justify-end items-end gap-2" data-testid={`msg-${msg.id}`}>
        <div className="max-w-[80%] bg-brand-blue text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-sm leading-relaxed shadow-sm whitespace-pre-wrap">
          {msg.text}
        </div>
        <div className="w-7 h-7 rounded-full bg-brand-blue/10 text-brand-blue flex items-center justify-center shrink-0">
          <User size={14} />
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-end gap-2" data-testid={`msg-${msg.id}`}>
      <div className="w-7 h-7 rounded-full bg-brand-green/15 text-brand-green flex items-center justify-center shrink-0">
        <Bot size={14} />
      </div>
      <div className="max-w-[85%] space-y-2">
        <div className="bg-white text-gray-800 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm leading-relaxed shadow-sm ring-1 ring-gray-100 whitespace-pre-wrap">
          {msg.text}
        </div>
        {msg.quickReplies && (
          <div className="flex flex-wrap gap-2">
            {msg.quickReplies.map((qr) => (
              <button
                key={qr.key}
                type="button"
                onClick={() => onQuickReply(qr.key, qr.label)}
                data-testid={`quick-${qr.key}`}
                className="text-sm px-3.5 py-1.5 rounded-full bg-brand-green/10 text-brand-green-dark hover:bg-brand-green hover:text-white font-medium transition-colors"
              >
                {qr.label}
              </button>
            ))}
          </div>
        )}
        {msg.actionButtons && (
          <div className="flex flex-wrap gap-2">
            {msg.actionButtons.map((btn) => (
              <a
                key={btn.key}
                href={btn.href}
                target={btn.href.startsWith('http') ? '_blank' : undefined}
                rel={btn.href.startsWith('http') ? 'noreferrer' : undefined}
                data-testid={`action-${btn.key}`}
                className={`text-sm px-4 py-2 rounded-full font-semibold text-white transition-colors ${
                  btn.color === 'green' ? 'bg-brand-green hover:bg-brand-green-dark' : 'bg-brand-blue hover:bg-brand-blue-light'
                }`}
              >
                {btn.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div className="flex items-end gap-2" data-testid="chatbot-typing">
      <div className="w-7 h-7 rounded-full bg-brand-green/15 text-brand-green flex items-center justify-center shrink-0">
        <Bot size={14} />
      </div>
      <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm ring-1 ring-gray-100">
        <div className="flex gap-1">
          <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}
